import { type KeyboardEvent, useId, useRef } from 'react';

export type ColorSwatchOption = {
  /** CSS color stored when the swatch is picked, e.g. `#1e88e5`. */
  value: string;
  /** Color name in French, announced and shown in the tooltip. */
  label: string;
};

export type ColorSwatchPickerProps = {
  /** Visible name of the choice. */
  label: string;
  /** Short explanation of where the color is used. */
  description?: string;
  options: ColorSwatchOption[];
  /** Picked color, `null` for the default one. */
  value: string | null;
  onChange: (value: string | null) => void;
  /** Adds a first swatch meaning "no specific color" (value `null`), named by this label. */
  defaultOptionLabel?: string;
  disabled?: boolean;
  'data-testid'?: string;
};

export type ColorSwatchItem = {
  key: string;
  value: string | null;
  label: string;
  selected: boolean;
  tabIndex: 0 | -1;
};

const NEXT_KEYS = ['ArrowRight', 'ArrowDown'];
const PREVIOUS_KEYS = ['ArrowLeft', 'ArrowUp'];
const DEFAULT_KEY = 'default';

export function getColorSwatchItems(props: Pick<ColorSwatchPickerProps, 'options' | 'value' | 'defaultOptionLabel'>) {
  const items: Omit<ColorSwatchItem, 'tabIndex'>[] = [
    ...(props.defaultOptionLabel === undefined
      ? []
      : [{ key: DEFAULT_KEY, value: null, label: props.defaultOptionLabel, selected: props.value === null }]),
    ...props.options.map((option) => ({
      key: option.value,
      value: option.value,
      label: option.label,
      selected: option.value.toLowerCase() === props.value?.toLowerCase(),
    })),
  ];
  const selectedIndex = items.findIndex((item) => item.selected);
  const focusableIndex = selectedIndex === -1 ? 0 : selectedIndex;

  return items.map((item, index): ColorSwatchItem => ({ ...item, tabIndex: index === focusableIndex ? 0 : -1 }));
}

/** Index reached from `index` with an arrow key (wrapping), or `null` for any other key. */
export function getNextColorSwatchIndex(index: number, count: number, key: string) {
  if (NEXT_KEYS.includes(key)) {
    return (index + 1) % count;
  }

  if (PREVIOUS_KEYS.includes(key)) {
    return (index - 1 + count) % count;
  }

  if (key === 'Home') {
    return 0;
  }

  if (key === 'End') {
    return count - 1;
  }

  return null;
}

export function useColorSwatchPicker(props: ColorSwatchPickerProps) {
  const labelId = useId();
  const descriptionId = useId();
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const items = getColorSwatchItems(props);

  function setButtonRef(index: number) {
    return (element: HTMLButtonElement | null) => {
      buttonRefs.current[index] = element;
    };
  }

  function handleSelect(value: string | null) {
    if (props.disabled) {
      return;
    }

    props.onChange(value);
  }

  function handleKeyDown(index: number, event: KeyboardEvent<HTMLButtonElement>) {
    const nextIndex = getNextColorSwatchIndex(index, items.length, event.key);

    if (nextIndex === null) {
      return;
    }

    event.preventDefault();
    buttonRefs.current[nextIndex]?.focus();
    handleSelect(items[nextIndex].value);
  }

  return {
    items,
    labelId,
    descriptionId,
    setButtonRef,
    handleSelect,
    handleKeyDown,
  };
}
